import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import BarChart3 from "lucide-react/dist/esm/icons/bar-chart-3";
import Building2 from "lucide-react/dist/esm/icons/building-2";
import CalendarDays from "lucide-react/dist/esm/icons/calendar-days";
import Hospital from "lucide-react/dist/esm/icons/hospital";
import Phone from "lucide-react/dist/esm/icons/phone";
import Settings from "lucide-react/dist/esm/icons/settings";
import Stethoscope from "lucide-react/dist/esm/icons/stethoscope";
import UserCircle from "lucide-react/dist/esm/icons/user-circle";
import Users from "lucide-react/dist/esm/icons/users";

import { ROUTES } from "@/constants/routes.constants";
import { cn, logoLayoutTransition, routes } from "@/lib/utils";
import { useUser } from "@/context/user-provider";
import { dashboards } from "@/content/analytics.content";

import { Logo } from "@/components/base/Logo";
import { ChatwootNavItem } from "@/components/module/ChatwootNavItem";
import { UserMenu } from "@/components/module/UserMenu";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { access, isSuperAdmin } = useUser();

  const navGroups: NavGroup[] = [
    {
      label: "Workspace",
      items: [
        ...(access.canManageAllPractitioners
          ? [
              {
                to: ROUTES.PRACTITIONERS,
                label: "Practitioners",
                icon: Stethoscope,
              },
            ]
          : []),
        ...(access.canManageOwnSchedule
          ? [
              {
                to: ROUTES.MY_SCHEDULE,
                label: "My Schedule",
                icon: CalendarDays,
              },
            ]
          : []),
      ],
    },
    ...(access.canViewAnalytics
      ? [
          {
            label: "Analytics",
            items: dashboards.map(({ id, label }) => ({
              to: routes.analyticsDashboard(id),
              label,
              icon: BarChart3,
            })),
          },
        ]
      : []),
    {
      label: "Organization",
      items: [
        { to: ROUTES.PROJECT, label: "Organization", icon: Building2 },
        ...(access.canReadMembers
          ? [{ to: ROUTES.TEAM, label: "Team", icon: Users }]
          : []),
        ...(isSuperAdmin
          ? [
              // t('Clinic Configuration')
              {
                to: ROUTES.CLINIC_CONFIGURATION,
                label: "Clinic Configuration",
                icon: Hospital,
              },
              // t('Agent Configuration')
              {
                to: ROUTES.AGENT_CONFIGURATION,
                label: "Agent Configuration",
                icon: Settings,
              },
            ]
          : []),
      ],
    },
    ...(access.canManageWhatsApp
      ? [
          {
            label: "Integrations",
            items: [{ to: ROUTES.WHATSAPP, label: "WhatsApp", icon: Phone }],
          },
        ]
      : []),
    {
      label: "Account",
      items: [{ to: ROUTES.PROFILE, label: "Profile", icon: UserCircle }],
    },
  ];

  return (
    <aside className="w-60 shrink-0">
      <div className="border-border bg-surface-subdued fixed top-0 left-0 flex h-screen w-60 flex-col border-r">
        <div className="border-border flex h-14 items-center border-b px-4">
          <motion.div layoutId="app-logo" transition={logoLayoutTransition}>
            <Logo className="h-8" />
          </motion.div>
        </div>
        <nav className="flex flex-1 flex-col gap-5 overflow-y-auto py-3">
          {navGroups.map((group) => (
            <div key={group.label} className="flex flex-col">
              <p className="text-subdued px-4 pb-1 text-[11px] font-semibold tracking-wider uppercase">
                {t(group.label)}
              </p>
              <div className="flex flex-col gap-0.5 px-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      viewTransition
                      className={({ isActive }) =>
                        cn(
                          "group flex items-center gap-2 rounded-lg",
                          "px-2 py-1.5 text-[13px] font-medium",
                          "text-text hover:bg-surface-hovered",
                          "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                          isActive && "bg-surface-selected text-primary"
                        )
                      }
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{t(item.label)}</span>
                    </NavLink>
                  );
                })}
                {group.label === "Workspace" && <ChatwootNavItem />}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-border border-t p-2">
          <UserMenu />
        </div>
      </div>
    </aside>
  );
};
